import { FormFieldDto, LearnerFieldAnswer, LearnerModuleAnswers } from '../../general/dtos';
import { isMultiChoiceType, selectionKeys } from './multi-choice-fields';

export function hasFilledAnswer(value: unknown): boolean {
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'boolean') return value;
    if (Array.isArray(value)) return value.some(hasFilledAnswer);
    if (value && typeof value === 'object') return Object.values(value).some(hasFilledAnswer);
    return typeof value === 'number' && Number.isFinite(value);
}

/** Convert the iframe's quiz-list composite keys into one answer per authored field. */
export function fieldAnswers(fields: FormFieldDto[], values: Record<string, any>): Record<string, LearnerFieldAnswer> {
    const answers: Record<string, LearnerFieldAnswer> = {};
    fields.forEach(field => {
        if (field.type === 'quiz-list') {
            const entries = Object.entries(values).filter(([key]) => key.startsWith(`${field.key}::`))
                .map(([key, value]) => [key.slice(field.key.length + 2), value]);
            if (entries.length) answers[field.key] = Object.fromEntries(entries);
        } else if (Object.prototype.hasOwnProperty.call(values, field.key) && values[field.key] !== undefined) {
            answers[field.key] = isMultiChoiceType(field.type) ? selectionKeys(values[field.key]).sort() : values[field.key];
        }
    });
    return answers;
}

function canonicalAnswer(value: LearnerFieldAnswer | undefined): string | undefined {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return JSON.stringify(Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b))));
    }
    return JSON.stringify(value);
}

export function changedFieldAnswers(fields: FormFieldDto[], before: Record<string, any>, after: Record<string, any>) {
    const baseline = fieldAnswers(fields, before);
    return Object.fromEntries(Object.entries(fieldAnswers(fields, after))
        .filter(([key, value]) => canonicalAnswer(value) !== canonicalAnswer(baseline[key])));
}

/** Restore the iframe format, including individual inline quiz dropdowns. */
export function iframeModuleAnswers(answers: LearnerModuleAnswers): Record<string, Record<string, any>> {
    return Object.fromEntries(Object.entries(answers).map(([fragmentKey, fields]) => [fragmentKey,
        Object.fromEntries(Object.entries(fields).flatMap(([key, value]) =>
            value && typeof value === 'object' && !Array.isArray(value)
                ? Object.entries(value).map(([target, selection]) => [`${key}::${target}`, selection])
                : [[key, value]]))]));
}
